/**
 * 招待QR（owner の発行画面と、本人の登録画面）の判断と QR の描画。
 *
 * ★コードは URL の**フラグメント**（`#invite=…`）に載せる。フラグメントはサーバへも
 *   `Referer` にも送られない。画面は読んだら**すぐ** `history.replaceState` で消す（`main.ts` の `start`）。
 * ★QR は**端末の中で**描く（`uqr`）。コードを外部のサービスへ送らない。
 * ★`innerHTML` を使わない。SVG も DOM で組む。
 */
import { encode } from "uqr";

import type { AccountInvite, InviteStaff } from "./api.js";
import { ROLE_LABEL } from "./account-review-view.js";

/** `src/auth/invite.ts` の `isInviteCodeShape` の写し（32バイトの base64url）。★突き合わせは `tests/ui/invite-view.test.ts` */
const CODE_PATTERN = /^[A-Za-z0-9_-]{43}$/u;
const HASH_PREFIX = "#invite=";

/**
 * 画面を開いた URL のフラグメントから招待のコードを読む。
 *
 * @returns 招待の URL でなければ `null`。**招待の URL なのにコードの形が違うときは空文字**
 *   （登録画面を出して「使えません」と言う。黙って通常のログイン画面へ落とすと、
 *   QR を読んだ本人が理由も分からず戸惑う）。
 */
export function inviteCodeFromHash(hash: string): string | null {
  if (!hash.startsWith(HASH_PREFIX)) return null;
  const code = hash.slice(HASH_PREFIX.length);
  return CODE_PATTERN.test(code) ? code : "";
}

/** 本人に渡す URL。★オリジンは呼び出し側が `location.origin` を渡す（開いている画面の版・ホストと同じ場所へ戻る） */
export const inviteUrl = (origin: string, code: string): string => `${origin}/${HASH_PREFIX}${code}`;

export const INVITE_UNUSABLE_TEXT =
  "この招待は使えません。すでに登録した方は、ログイン画面から入れます。招待した人に、新しい招待を出してもらってください";

/** 時給・個人別人件費を見られる役割（`roles.can_view_payroll`）。★写し。判定の根拠は DB、ここは注意喚起の出し分けだけ */
export const inviteRoleSeesPayroll = (role: string): boolean => role === "manager" || role === "owner";

export const INVITE_PAYROLL_WARNING =
  "このQRを読んだ人は、時給・個人別人件費を見られるアカウントを作れます（オーナーは全権限）。" +
  "本人の目の前で出し、他の人に見せないでください。";

export const inviteTargetLabel = (target: InviteStaff | null): string =>
  target === null ? "対象なし（後で手動紐付け）" : `対象：${target.staffCode} ${target.displayName}`;

export interface InviteRowView {
  id: string;
  headline: string;
  detail: string;
}

/** 未使用の招待の1行。★コードは持っていない（DB はハッシュしか持たないので再表示できない） */
export function inviteRowViews(invites: AccountInvite[]): InviteRowView[] {
  return invites.map((i) => ({
    id: i.id,
    headline: `${ROLE_LABEL[i.role]}の招待`,
    detail: `${inviteTargetLabel(i.targetStaff)}／ 発行 ${i.issuedAt}（${i.issuedByLoginId}）／ 期限 ${i.expiresAt}`,
  }));
}

/** 登録画面の入力を送る前に見る（サーバも同じ決まりで断る。画面で先に言うのは打ち直しを短くするため） */
export function registerFormError(input: { password: string; confirm: string }): string | null {
  return input.password === input.confirm ? null : "パスワードが確認の入力と一致しません";
}

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * URL を QR の SVG にする。
 * ★読み取りの安定のため、明暗は**画面のテーマに依らず**白地に黒で固定する（ダークでも読める）。
 */
export function qrSvg(text: string): SVGSVGElement {
  const { data, size } = encode(text, { ecc: "M", border: 2 });
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", "招待のQRコード");
  svg.setAttribute("shape-rendering", "crispEdges");
  svg.classList.add("invite-qr");
  const bg = document.createElementNS(SVG_NS, "rect");
  bg.setAttribute("width", String(size));
  bg.setAttribute("height", String(size));
  bg.setAttribute("fill", "#ffffff");
  const path = document.createElementNS(SVG_NS, "path");
  let d = "";
  data.forEach((row, y) =>
    row.forEach((dark, x) => {
      if (dark) d += `M${x} ${y}h1v1h-1z`;
    }),
  );
  path.setAttribute("d", d);
  path.setAttribute("fill", "#000000");
  svg.append(bg, path);
  return svg;
}
